import { isCommercialTermsProduct, isCommitmentProduct, isOneTimeProduct } from '@vy/services';
import { NewProductSchema } from '@vy/types';
import { withDefault } from '@vy/utils';
import { useCallback } from 'react';
import { get } from 'react-hook-form';
import type { PlanInputForm } from '../../../../pages/plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { PLAN_CREATION_REASONS } from '../../create-plan-context-types/plan-create-reason';
import type { PlanCreationFormUtils } from '../create-plan-provider-types/plan-creation-form-utils';
import { useCreatePlanContext } from '../..';

export function usePlanCreationFormUtils(): PlanCreationFormUtils {
  const { existingPlan, planCreationReason } = useCreatePlanContext();

  const handleIsProductExist = useCallback(
    (productId: string) => {
      const plan = withDefault<Pick<PlanInputForm, 'fixedProducts' | 'usageProducts'>>(existingPlan, {
        fixedProducts: [],
        usageProducts: [],
      });

      return isProductExist(plan, productId);
    },
    [existingPlan],
  );

  const handleIsFieldEditable = useCallback(
    (field: string) => {
      const fieldValue = get(existingPlan, field);

      const parsedNewProduct = NewProductSchema.safeParse(fieldValue);

      if (parsedNewProduct.success) {
        return !(
          planCreationReason === PLAN_CREATION_REASONS.AddPhaseToCurrentContract &&
          (isOneTimeProduct(parsedNewProduct.data) ||
            isCommitmentProduct(parsedNewProduct.data) ||
            isCommercialTermsProduct(parsedNewProduct.data))
        );
      }

      return true;
    },
    [existingPlan, planCreationReason],
  );

  return {
    planCreationReason,
    isProductExist: handleIsProductExist,
    isFieldEditable: handleIsFieldEditable,
  };
}

function isProductExist(
  existingPlan: Pick<PlanInputForm, 'fixedProducts' | 'usageProducts'>,
  productId: string,
) {
  const fixedProducts = withDefault(existingPlan.fixedProducts, []);
  const usageProducts = withDefault(existingPlan.usageProducts, []);
  const products = [...fixedProducts, ...usageProducts];

  return products.some(({ id }) => id === productId);
}
